import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ArrowLeft, ArrowRight, Crosshair, Home, Maximize2, Monitor, RotateCw, Smartphone, X } from "lucide-react";
import {
  BRAND_SITE_PAGE_TREE_ID,
  BrandSitePageTree,
} from "@/components/brand-site/BrandSitePageTree";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PickRefChipInput, type PickRefChipInputHandle } from "@/components/brand-site/PickRefChipInput";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { BrandSiteCollection, BrandSitePage } from "@/lib/brand-site-api";
import { previewBrandSitePage } from "@/lib/brand-site-api";
import { buildPageTree, addressBarTreeQuery } from "@/lib/brand-site-page-tree";
import {
  applyPreviewSrcDoc,
  resolvePreviewIframePaint,
  shouldForcePreviewSrcDocReload,
  markPreviewIframeReady,
} from "@/lib/brand-site-preview-morph";
import {
  pickRefFromPayload,
  type PickRef,
} from "@/lib/brand-site-pick-refs";
import {
  BRAND_SITE_PREVIEW_MESSAGE,
  BRAND_SITE_PICK_HOVER_EVENT,
  clampPickPopoverPosition,
  homeSlugFromPages,
  injectPreviewBridge,
  isPreviewNavMessage,
  isPreviewPickClearMessage,
  isPreviewPickMessage,
  missingPreviewPageHtml,
  normalizePreviewPickPayload,
  parsePreviewHref,
  pathForCollectionListPreview,
  pathForPreviewSlug,
  isBrandSiteCollectionNavSlug,
  shouldUseBrandSitePreviewApi,
} from "@/lib/brand-site-preview-nav";
import { rewriteC456AssetPathsInHtml } from "@/lib/hermes-html-preview";

type Viewport = "desktop" | "mobile";

type NavState = { stack: string[]; index: number };

type PickPanelState = {
  picks: PickRef[];
  rect: { top: number; left: number; width: number; height: number };
  top: number;
  left: number;
};

type Props = {
  accountId?: string | number;
  brandSiteId?: string | number;
  pages: BrandSitePage[];
  collections?: BrandSiteCollection[];
  selectedSlug: string | null;
  /** Hermes 草稿写入后递增；并入 identity，强制 iframe 重挂，避免 sandboxed morph 静默失败 */
  draftRevision?: number;
  /** 品牌站公网 origin；改写 srcdoc 内 /_c456/a/…，否则相对路径在 iframe 里裂图 */
  assetBaseUrl?: string | null;
  onSelectSlug: (slug: string) => void;
  /** 点选浮层提交；调用方负责开 AI 官网并 sendMessage */
  onPickSubmit?: (payload: {
    userRequest: string;
    pageSlug: string;
    pageTitle: string | null;
    pagePath: string;
    picks: PickRef[];
  }) => Promise<void> | void;
  pickSubmitting?: boolean;
};

/** 全高官网预览：只显示已写入草稿的页面，不用对话里的 HTML。点选默认开。 */
export function BrandSiteLivePreview({
  accountId,
  brandSiteId,
  pages,
  collections = [],
  selectedSlug,
  draftRevision = 0,
  assetBaseUrl = null,
  onSelectSlug,
  onPickSubmit,
  pickSubmitting = false,
}: Props) {
  const [viewport, setViewport] = useState<Viewport>("desktop");
  const [pickEnabled, setPickEnabled] = useState(true);
  const [pickPanel, setPickPanel] = useState<PickPanelState | null>(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const pickFormRef = useRef<HTMLFormElement>(null);
  const pickInputRef = useRef<PickRefChipInputHandle>(null);
  const homeSlug = homeSlugFromPages(pages);
  const treeRoot = useMemo(
    () =>
      buildPageTree(
        pages,
        homeSlug,
        collections.map((collection) => ({ key: collection.key, nav_label: collection.nav_label })),
      ),
    [collections, homeSlug, pages],
  );
  const selected = useMemo(() => {
    if (pages.length === 0) return null;
    if (selectedSlug) {
      const match = pages.find((page) => page.slug === selectedSlug);
      if (match) return match;
    }
    return pages.find((page) => page.is_home) ?? pages[0] ?? null;
  }, [pages, selectedSlug]);

  const [nav, setNav] = useState<NavState>({
    stack: [selectedSlug || homeSlug],
    index: 0,
  });
  const [missingPath, setMissingPath] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [urlDraft, setUrlDraft] = useState("/");
  const [treeOpen, setTreeOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewError, setPreviewError] = useState<string | null>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);

  const viewingSlug = nav.stack[nav.index] ?? selected?.slug ?? homeSlug;
  /** 手动刷新或草稿 revision 变更才重挂 iframe；换页不重挂，避免空 iframe + 双写 srcdoc 白屏 */
  const previewIdentityKey = `${reloadToken}:${draftRevision}`;
  // 勿回落到旧 selected：切到 /blog/:slug 等栏目路由时 selected 仍可能是首页，会挡住 preview API
  const viewingPage =
    pages.find((page) => page.slug === viewingSlug) ??
    (selected?.slug === viewingSlug ? selected : null);
  const viewingNode = useMemo(() => {
    function findNode(node: typeof treeRoot): (typeof treeRoot)["children"][number] | undefined {
      if (node.slug === viewingSlug) return node;
      // 页面列表「在设计页预览」传 blog*，版式树节点 slug 是 blog
      if (
        node.kind === "collection_list" &&
        (viewingSlug === `${node.slug}*` || viewingSlug === node.path.replace(/^\//, ""))
      ) {
        return node;
      }
      for (const child of node.children) {
        const hit = findNode(child);
        if (hit) return hit;
      }
      return undefined;
    }
    return findNode(treeRoot);
  }, [treeRoot, viewingSlug]);
  const collectionRoute = useMemo(
    () =>
      viewingSlug.endsWith("/:slug") || isBrandSiteCollectionNavSlug(viewingSlug, collections),
    [collections, viewingSlug],
  );
  const needsPreviewApi = shouldUseBrandSitePreviewApi({
    hasAccountAndSite: accountId != null && brandSiteId != null,
    viewingSlug,
    hasViewingPage: Boolean(viewingPage),
    collectionRoute,
    viewingNodeKind: viewingNode?.kind ?? null,
  });
  const previewRequestPath = useMemo(() => {
    if (missingPath) return missingPath;
    if (!viewingSlug || viewingSlug === homeSlug) return "/";
    if (viewingSlug.startsWith("_")) return `/${viewingSlug}`;
    if (
      viewingNode?.kind === "collection_list" ||
      collections.some((c) => viewingSlug === c.key || viewingSlug === `${c.key}*`)
    ) {
      return pathForCollectionListPreview(viewingSlug);
    }
    return pathForPreviewSlug(viewingSlug, homeSlug);
  }, [collections, homeSlug, missingPath, viewingNode?.kind, viewingSlug]);

  useEffect(() => {
    if (!needsPreviewApi || accountId == null || brandSiteId == null) {
      setPreviewHtml("");
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    // 换路径只清空状态；加载中外层遮罩，勿往 iframe 写「加载预览…」占位（双写 srcdoc 易白屏）
    setPreviewHtml("");
    setPreviewError(null);
    void previewBrandSitePage(accountId, brandSiteId, previewRequestPath)
      .then((preview) => {
        if (cancelled) return;
        setPreviewHtml(preview.html);
        setPreviewError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewHtml("");
        setPreviewError("预览加载失败，请点刷新重试");
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, brandSiteId, draftRevision, needsPreviewApi, previewRequestPath, reloadToken]);

  const iframePaint = useMemo(
    () =>
      resolvePreviewIframePaint({
        missingHtml: missingPath ? missingPreviewPageHtml(missingPath) : null,
        needsPreviewApi,
        previewHtml,
        previewError,
        staticHtml: viewingPage?.html_content?.trim() || "",
      }),
    [missingPath, needsPreviewApi, previewError, previewHtml, viewingPage?.html_content],
  );
  const previewLoading = iframePaint.mode === "hold";
  // hold 时 rawHtml 为空：布局 effect 跳过写入，保留上一页；外层显示加载遮罩
  const rawHtml = iframePaint.mode === "document" ? iframePaint.html : "";
  const srcDoc = useMemo(
    () =>
      rawHtml
        ? injectPreviewBridge(rewriteC456AssetPathsInHtml(rawHtml, assetBaseUrl), { pick: true })
        : "",
    [assetBaseUrl, rawHtml],
  );
  /** 全屏只看效果，不挂点选→AI，避免挡聊天且无右栏可用 */
  const fullscreenSrcDoc = useMemo(
    () =>
      rawHtml
        ? injectPreviewBridge(rewriteC456AssetPathsInHtml(rawHtml, assetBaseUrl), { pick: false })
        : "",
    [assetBaseUrl, rawHtml],
  );
  const identityRef = useRef(previewIdentityKey);
  const appliedSrcDocRef = useRef<string | null>(null);
  /** 跟踪 iframe DOM 节点；节点被换掉时必须重写 srcdoc，否则会白屏 */
  const iframeHostRef = useRef<HTMLIFrameElement | null>(null);
  const appliedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (pages.length === 0 && collections.length === 0) return;
    setNav((current) => {
      const slug = current.stack[current.index];
      if (
        slug &&
        (pages.some((page) => page.slug === slug) ||
          isBrandSiteCollectionNavSlug(slug, collections))
      ) {
        return current;
      }
      const fallback = selected?.slug ?? homeSlug;
      if (current.stack.length === 1 && current.stack[0] === fallback) return current;
      return { stack: [fallback], index: 0 };
    });
  }, [collections, homeSlug, pages, selected?.slug]);

  // 左侧文件树 / 父组件改 selectedSlug 时，同步预览导航（预览自有 nav，不会自动跟）
  useEffect(() => {
    if (!selectedSlug) return;
    setNav((current) => {
      if (current.stack[current.index] === selectedSlug) return current;
      const stack = [...current.stack.slice(0, current.index + 1), selectedSlug];
      return { stack, index: stack.length - 1 };
    });
    setMissingPath(null);
  }, [selectedSlug]);

  const displayPath = useMemo(() => {
    if (missingPath) return missingPath;
    if (
      viewingNode?.kind === "collection_list" ||
      collections.some((c) => viewingSlug === c.key || viewingSlug === `${c.key}*`)
    ) {
      const key = viewingSlug.replace(/\*+$/, "");
      return `/${key}*`;
    }
    if (viewingNode?.kind === "collection_item") {
      return viewingNode.path;
    }
    return pathForPreviewSlug(viewingSlug, homeSlug);
  }, [collections, homeSlug, missingPath, viewingNode, viewingSlug]);

  useEffect(() => {
    setUrlDraft(displayPath);
  }, [displayPath]);

  const clearPickPanel = useCallback(() => {
    setPickPanel(null);
    iframeRef.current?.contentWindow?.postMessage(
      { source: BRAND_SITE_PREVIEW_MESSAGE, type: "pick-clear" },
      "*",
    );
  }, []);

  const syncPickPaths = useCallback((paths: string[]) => {
    iframeRef.current?.contentWindow?.postMessage(
      { source: BRAND_SITE_PREVIEW_MESSAGE, type: "pick-sync", paths },
      "*",
    );
  }, []);

  useEffect(() => {
    clearPickPanel();
  }, [viewingSlug, reloadToken, clearPickPanel]);

  useEffect(() => {
    if (!pickEnabled) clearPickPanel();
  }, [clearPickPanel, pickEnabled]);

  const syncPickToggle = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      {
        source: BRAND_SITE_PREVIEW_MESSAGE,
        type: "pick-toggle",
        enabled: pickEnabled,
      },
      "*",
    );
  }, [pickEnabled]);

  useLayoutEffect(() => {
    if (pages.length === 0 && collections.length === 0) {
      appliedSrcDocRef.current = null;
      iframeHostRef.current = null;
      appliedPathRef.current = null;
      return;
    }
    // API 加载中：保留上一页 srcdoc，外层遮罩；禁止写占位再写真 HTML（双写易白屏）
    if (previewLoading || !srcDoc) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    // iframe 因路由切回 / key 变化被换成新节点时，旧 applied 不能挡住写入
    if (iframeHostRef.current !== iframe) {
      iframeHostRef.current = iframe;
      appliedSrcDocRef.current = null;
      appliedPathRef.current = null;
    }

    const identityChanged = identityRef.current !== previewIdentityKey;
    if (identityChanged) {
      identityRef.current = previewIdentityKey;
      appliedSrcDocRef.current = null;
      appliedPathRef.current = null;
    }

    if (appliedSrcDocRef.current === srcDoc) return;

    const pathChanged =
      appliedPathRef.current != null && appliedPathRef.current !== previewRequestPath;
    const forceReload = shouldForcePreviewSrcDocReload(appliedSrcDocRef.current, srcDoc, {
      pathChanged,
    });
    appliedSrcDocRef.current = srcDoc;
    appliedPathRef.current = previewRequestPath;
    const result = applyPreviewSrcDoc(iframe, srcDoc, { forceReload });
    if (result === "morphed") syncPickToggle();
  }, [
    collections.length,
    pages.length,
    previewIdentityKey,
    previewLoading,
    previewRequestPath,
    srcDoc,
    syncPickToggle,
  ]);

  useEffect(() => {
    syncPickToggle();
  }, [syncPickToggle, reloadToken]);

  useEffect(() => {
    function onChipHover(event: Event) {
      const path =
        (event as CustomEvent<{ path: string | null }>).detail?.path ?? null;
      iframeRef.current?.contentWindow?.postMessage(
        {
          source: BRAND_SITE_PREVIEW_MESSAGE,
          type: "pick-hover",
          path: path || null,
        },
        "*",
      );
    }
    window.addEventListener(BRAND_SITE_PICK_HOVER_EVENT, onChipHover);
    return () => {
      window.removeEventListener(BRAND_SITE_PICK_HOVER_EVENT, onChipHover);
      iframeRef.current?.contentWindow?.postMessage(
        { source: BRAND_SITE_PREVIEW_MESSAGE, type: "pick-hover", path: null },
        "*",
      );
    };
  }, []);

  const placePickPanel = useCallback((payload: NonNullable<ReturnType<typeof normalizePreviewPickPayload>>) => {
    const iframe = iframeRef.current;
    const stage = stageRef.current;
    if (!iframe || !stage) return;
    const picks = payload.items.map(pickRefFromPayload);
    const active = payload.items[payload.activeIndex] ?? payload.items[payload.items.length - 1];
    if (!active) return;
    const iframeRect = iframe.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const measured = pickFormRef.current?.getBoundingClientRect();
    const pos = clampPickPopoverPosition({
      stageWidth: stageRect.width,
      stageHeight: stageRect.height,
      iframeOffsetTop: iframeRect.top - stageRect.top,
      iframeOffsetLeft: iframeRect.left - stageRect.left,
      rect: active.rect,
      popoverWidth: measured?.width ?? Math.min(360, stageRect.width - 16),
      popoverHeight: measured?.height ?? 168,
    });
    setPickPanel({
      picks,
      rect: active.rect,
      top: pos.top,
      left: pos.left,
    });
  }, []);

  useEffect(() => {
    if (!pickPanel) return;
    const stage = stageRef.current;
    const iframe = iframeRef.current;
    const form = pickFormRef.current;
    if (!stage || !iframe || !form) return;

    const reposition = () => {
      const iframeRect = iframe.getBoundingClientRect();
      const stageRect = stage.getBoundingClientRect();
      const formRect = form.getBoundingClientRect();
      const pos = clampPickPopoverPosition({
        stageWidth: stageRect.width,
        stageHeight: stageRect.height,
        iframeOffsetTop: iframeRect.top - stageRect.top,
        iframeOffsetLeft: iframeRect.left - stageRect.left,
        rect: pickPanel.rect,
        popoverWidth: formRect.width,
        popoverHeight: formRect.height,
      });
      setPickPanel((current) =>
        current && (current.top !== pos.top || current.left !== pos.left)
          ? { ...current, top: pos.top, left: pos.left }
          : current,
      );
    };

    reposition();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(reposition) : null;
    ro?.observe(stage);
    ro?.observe(form);
    window.addEventListener("resize", reposition);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", reposition);
    };
  }, [pickPanel?.rect, pickPanel?.picks.length]);

  const pushSlug = useCallback((slug: string) => {
    setNav((current) => {
      if (current.stack[current.index] === slug) return current;
      const stack = [...current.stack.slice(0, current.index + 1), slug];
      return { stack, index: stack.length - 1 };
    });
  }, []);

  const navigateTo = useCallback(
    (slug: string, opts?: { index?: number }) => {
      const page = pages.find((item) => item.slug === slug);
      const collectionRouteHit = isBrandSiteCollectionNavSlug(slug, collections);
      if (opts?.index != null) {
        setNav((current) => ({ ...current, index: opts.index! }));
      } else {
        pushSlug(slug);
      }
      if (!page && !collectionRouteHit) {
        const isHomeDraft = pages.length === 0 && (slug === homeSlug || slug === "index");
        setMissingPath(isHomeDraft ? null : pathForPreviewSlug(slug, homeSlug));
        return;
      }
      setMissingPath(null);
      onSelectSlug(page?.slug ?? slug);
    },
    [collections, homeSlug, onSelectSlug, pages, pushSlug],
  );

  const handlePreviewHref = useCallback(
    (href: string) => {
      const parsed = parsePreviewHref(href, homeSlug);
      if (parsed.kind === "hash" || parsed.kind === "ignore") return;
      if (parsed.kind === "external") {
        window.open(parsed.url, "_blank", "noopener,noreferrer");
        return;
      }
      navigateTo(parsed.slug);
    },
    [homeSlug, navigateTo],
  );

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (isPreviewPickClearMessage(event.data)) {
        setPickPanel(null);
        return;
      }
      if (isPreviewPickMessage(event.data)) {
        const normalized = normalizePreviewPickPayload(event.data);
        if (normalized) placePickPanel(normalized);
        return;
      }
      if (!isPreviewNavMessage(event.data)) return;
      handlePreviewHref(event.data.href);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [handlePreviewHref, placePickPanel]);

  const pickBundle = useMemo(() => {
    if (!pickPanel || !viewingPage) return null;
    return {
      pageSlug: viewingPage.slug,
      pageTitle: viewingPage.title,
      pagePath: displayPath,
      picks: pickPanel.picks,
    };
  }, [displayPath, pickPanel, viewingPage]);

  function handlePicksChange(picks: import("@/lib/brand-site-pick-refs").PickRef[]) {
    if (!pickPanel) return;
    if (picks.length === 0) {
      clearPickPanel();
      return;
    }
    setPickPanel({ ...pickPanel, picks });
    syncPickPaths(picks.map((ref) => ref.path));
  }

  const canBack = nav.index > 0;
  const canForward = nav.index < nav.stack.length - 1;

  function goBack() {
    if (!canBack) return;
    const nextIndex = nav.index - 1;
    const slug = nav.stack[nextIndex];
    if (!slug) return;
    navigateTo(slug, { index: nextIndex });
  }

  function goForward() {
    if (!canForward) return;
    const nextIndex = nav.index + 1;
    const slug = nav.stack[nextIndex];
    if (!slug) return;
    navigateTo(slug, { index: nextIndex });
  }

  function submitUrl(event: FormEvent) {
    event.preventDefault();
    const value = urlDraft.trim() || "/";
    handlePreviewHref(value.startsWith("/") || /^[a-z]+:/i.test(value) ? value : `/${value}`);
    setTreeOpen(false);
  }

  function openPageTree() {
    setTreeOpen(true);
  }

  function selectPageFromTree(slug: string) {
    navigateTo(slug);
    setTreeOpen(false);
  }

  async function submitPick(event: FormEvent) {
    event.preventDefault();
    const content = pickInputRef.current?.getContent();
    const request = content?.userRequest.trim() ?? "";
    if (!request || !content || content.picks.length === 0 || !onPickSubmit || pickSubmitting) return;
    await onPickSubmit({
      userRequest: request,
      pageSlug: content.pageSlug || pickBundle!.pageSlug,
      pageTitle: content.pageTitle ?? pickBundle!.pageTitle,
      pagePath: content.pagePath || pickBundle!.pagePath,
      picks: content.picks,
    });
    clearPickPanel();
  }

  const empty = pages.length === 0 && collections.length === 0;
  const frameTitle = viewingPage?.title ?? "预览";

  const handleIframeLoad = useCallback(() => {
    markPreviewIframeReady(iframeRef.current);
    syncPickToggle();
  }, [syncPickToggle]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {empty ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-6 text-center">
          <p className="max-w-sm text-sm text-muted-foreground">
            还没有页面。打开「AI 官网」后跟助手说要做官网，写入后这里才会出现预览。
          </p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-background">
          <div className="flex shrink-0 items-center gap-1 border-b border-border bg-muted/40 px-1.5 py-1">
            <IconButton label="后退" disabled={!canBack} onClick={goBack}>
              <ArrowLeft className="size-3.5" />
            </IconButton>
            <IconButton label="前进" disabled={!canForward} onClick={goForward}>
              <ArrowRight className="size-3.5" />
            </IconButton>
            <IconButton label="首页" onClick={() => navigateTo(homeSlug)}>
              <Home className="size-3.5" />
            </IconButton>
            <IconButton label="刷新预览" onClick={() => setReloadToken((token) => token + 1)}>
              <RotateCw className="size-3.5" />
            </IconButton>
            <Popover open={treeOpen} onOpenChange={setTreeOpen} modal={false}>
              <PopoverAnchor asChild>
                <form onSubmit={submitUrl} className="min-w-0 flex-1">
                  <input
                    ref={urlInputRef}
                    value={urlDraft}
                    onChange={(event) => setUrlDraft(event.target.value)}
                    onFocus={openPageTree}
                    onClick={openPageTree}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setUrlDraft(displayPath);
                        setTreeOpen(false);
                      }
                    }}
                    aria-label="预览地址"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={treeOpen}
                    aria-controls={BRAND_SITE_PAGE_TREE_ID}
                    autoComplete="off"
                    spellCheck={false}
                    className="h-7 w-full rounded-md border border-border bg-background px-2 font-mono text-xs text-foreground outline-none focus:border-primary"
                  />
                </form>
              </PopoverAnchor>
              <PopoverContent
                align="start"
                side="bottom"
                sideOffset={4}
                className="w-[var(--radix-popover-trigger-width)] max-w-none rounded-lg p-0"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                onInteractOutside={(event) => {
                  if (urlInputRef.current?.contains(event.target as Node)) {
                    event.preventDefault();
                  }
                }}
              >
                <BrandSitePageTree
                  pages={pages}
                  collections={collections}
                  homeSlug={homeSlug}
                  activeSlug={viewingPage?.slug ?? viewingSlug}
                  missingPath={missingPath}
                  query={addressBarTreeQuery(urlDraft, displayPath)}
                  onNavigate={selectPageFromTree}
                />
              </PopoverContent>
            </Popover>
            {pickEnabled ? (
              <span className="shrink-0 px-1 text-[11px] text-muted-foreground">
                点选中 · ⌘/Ctrl 加选
              </span>
            ) : null}
            <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
              <IconButton
                label={pickEnabled ? "点选开着：点页面元素改内容" : "点选已关：可点链接跳转"}
                ariaLabel="点选改页"
                pressed={pickEnabled}
                pressedClassName="bg-primary/15 text-foreground"
                onClick={() => setPickEnabled((value) => !value)}
              >
                <Crosshair className="size-3.5" />
              </IconButton>
              <IconButton
                label="桌面预览"
                pressed={viewport === "desktop"}
                onClick={() => setViewport("desktop")}
              >
                <Monitor className="size-3.5" />
              </IconButton>
              <IconButton
                label="手机预览"
                pressed={viewport === "mobile"}
                onClick={() => setViewport("mobile")}
              >
                <Smartphone className="size-3.5" />
              </IconButton>
              <IconButton
                label="全屏预览"
                disabled={!rawHtml.trim()}
                onClick={() => setFullscreenOpen(true)}
              >
                <Maximize2 className="size-3.5" />
              </IconButton>
            </div>
          </div>
          <div
            ref={stageRef}
            className="relative flex min-h-0 flex-1 items-stretch justify-center bg-neutral-200/70"
          >
            <iframe
              ref={iframeRef}
              key={previewIdentityKey}
              title={frameTitle}
              sandbox="allow-scripts"
              onLoad={handleIframeLoad}
              className={cn(
                "h-full bg-white",
                viewport === "mobile"
                  ? "mx-auto w-[390px] max-w-full shadow-lg"
                  : "w-full",
              )}
            />
            {previewLoading ? (
              <div
                className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/80"
                aria-live="polite"
              >
                <p className="text-sm text-muted-foreground">加载预览…</p>
              </div>
            ) : null}
            {pickBundle && onPickSubmit ? (
              <form
                ref={pickFormRef}
                onSubmit={(event) => void submitPick(event)}
                className="absolute z-20 w-[min(100%-1rem,22rem)] max-w-full p-0 shadow-none"
                style={{ top: pickPanel!.top, left: pickPanel!.left }}
              >
                <PickRefChipInput
                  ref={pickInputRef}
                  bundle={pickBundle}
                  onPicksChange={handlePicksChange}
                  onSubmit={() => pickFormRef.current?.requestSubmit()}
                  onCancel={clearPickPanel}
                  submitting={pickSubmitting}
                  autoFocus
                />
              </form>
            ) : null}
          </div>
        </div>
      )}

      <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
        <DialogContent
          className="flex h-[min(96vh,920px)] w-full max-w-[min(98vw,1280px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(98vw,1280px)]"
          showCloseButton={false}
        >
          {/* 与聊天区 Hermes 全屏区分：标题+地址栏；关闭与标题同行；全屏关闭点选 AI */}
          <DialogHeader className="sr-only">
            <DialogTitle>{frameTitle} · 全屏预览</DialogTitle>
            <DialogDescription>当前路径 {displayPath}</DialogDescription>
          </DialogHeader>

          <div className="shrink-0 space-y-2 border-b border-border px-3 py-2.5">
            <div className="flex items-center gap-2">
              <p className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight">
                {frameTitle}
              </p>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="size-8 shrink-0"
                  aria-label="关闭全屏预览"
                >
                  <X className="size-4" />
                </Button>
              </DialogClose>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border bg-muted/40 px-1.5 py-1">
              <IconButton label="后退" disabled={!canBack} onClick={goBack}>
                <ArrowLeft className="size-3.5" />
              </IconButton>
              <IconButton label="前进" disabled={!canForward} onClick={goForward}>
                <ArrowRight className="size-3.5" />
              </IconButton>
              <IconButton label="首页" onClick={() => navigateTo(homeSlug)}>
                <Home className="size-3.5" />
              </IconButton>
              <IconButton label="刷新预览" onClick={() => setReloadToken((token) => token + 1)}>
                <RotateCw className="size-3.5" />
              </IconButton>
              <form
                onSubmit={submitUrl}
                className="min-w-0 flex-1"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  value={urlDraft}
                  onChange={(event) => setUrlDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") setUrlDraft(displayPath);
                  }}
                  aria-label="预览地址"
                  autoComplete="off"
                  spellCheck={false}
                  className="h-7 w-full rounded-md border border-border bg-background px-2 font-mono text-xs text-foreground outline-none focus:border-primary"
                />
              </form>
              <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                <IconButton
                  label="桌面预览"
                  pressed={viewport === "desktop"}
                  onClick={() => setViewport("desktop")}
                >
                  <Monitor className="size-3.5" />
                </IconButton>
                <IconButton
                  label="手机预览"
                  pressed={viewport === "mobile"}
                  onClick={() => setViewport("mobile")}
                >
                  <Smartphone className="size-3.5" />
                </IconButton>
              </div>
            </div>
          </div>

          <div className="relative flex min-h-0 flex-1 items-stretch justify-center overflow-hidden bg-neutral-200/70 p-2">
            {rawHtml.trim() ? (
              <iframe
                title={`${frameTitle} 全屏预览`}
                sandbox="allow-scripts"
                srcDoc={fullscreenSrcDoc}
                className={cn(
                  "h-full rounded-md border border-border bg-white",
                  viewport === "mobile" ? "mx-auto w-[390px] max-w-full shadow-lg" : "w-full",
                )}
              />
            ) : previewLoading ? (
              <div className="flex items-center justify-center px-4 text-sm text-muted-foreground">
                加载预览…
              </div>
            ) : (
              <div className="flex items-center justify-center px-4 text-sm text-muted-foreground">
                暂无预览内容
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function IconButton({
  label,
  ariaLabel,
  disabled,
  pressed,
  pressedClassName,
  onClick,
  children,
}: {
  label: string;
  ariaLabel?: string;
  disabled?: boolean;
  pressed?: boolean;
  pressedClassName?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel ?? label}
      aria-pressed={pressed}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30",
        pressed && (pressedClassName ?? "bg-muted text-foreground"),
      )}
    >
      {children}
    </button>
  );
}
